import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { CATEGORY_ORDER } from "@/data/clinic";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CalendarCheck, Sparkles, Settings, LogOut, Plus, Pencil, Trash2, Phone, Mail, Clock, MapPin, Save,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-blue-100 text-blue-800",
};

const emptyTreatment = {
  name: "", category: CATEGORY_ORDER[0], description: "", price: 0, currency: "£",
  duration_minutes: 30, image: "", active: true, order: 0,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState("bookings");

  const [bookings, setBookings] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [settings, setSettings] = useState({ booking_email: "", clinic_name: "", contact_phone: "" });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tForm, setTForm] = useState(emptyTreatment);

  const loadAll = () => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/treatments?include_inactive=true").then((r) => setTreatments(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };

  useEffect(loadAll, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin");
  };

  // ---- Bookings ----
  const setBookingStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Booking deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  // ---- Treatments ----
  const openCreate = () => { setEditing(null); setTForm(emptyTreatment); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setTForm({ ...t }); setDialogOpen(true); };

  const saveTreatment = async () => {
    const payload = {
      ...tForm,
      price: Number(tForm.price),
      duration_minutes: Number(tForm.duration_minutes),
      order: Number(tForm.order) || 0,
    };
    try {
      if (editing) {
        await api.put(`/treatments/${editing.id}`, payload);
        toast.success("Treatment updated");
      } else {
        await api.post("/treatments", payload);
        toast.success("Treatment added");
      }
      setDialogOpen(false);
      api.get("/treatments?include_inactive=true").then((r) => setTreatments(r.data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const deleteTreatment = async (id) => {
    if (!window.confirm("Delete this treatment?")) return;
    try {
      await api.delete(`/treatments/${id}`);
      setTreatments((t) => t.filter((x) => x.id !== id));
      toast.success("Treatment deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  // ---- Settings ----
  const saveSettings = async () => {
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  const NAV = [
    { id: "bookings", label: "Bookings", icon: CalendarCheck, badge: pendingCount },
    { id: "treatments", label: "Treatments", icon: Sparkles },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-brand-cream grain flex">
      {/* Sidebar */}
      <aside className="w-64 bg-brand-greenDark text-brand-cream flex-col hidden md:flex">
        <div className="p-6 border-b border-brand-cream/10">
          <Logo light compact />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              data-testid={`admin-tab-${n.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                tab === n.id ? "bg-brand-gold text-white" : "text-brand-cream/70 hover:bg-brand-cream/10"
              }`}
            >
              <n.icon size={18} /> {n.label}
              {n.badge > 0 && <span className="ml-auto bg-white/20 text-xs rounded-full px-2 py-0.5">{n.badge}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-brand-cream/10">
          <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-brand-cream/70 hover:bg-brand-cream/10">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col">
        {/* Mobile top nav */}
        <div className="md:hidden bg-brand-greenDark text-brand-cream p-4 flex items-center justify-between">
          <Logo light compact />
          <button onClick={doLogout} data-testid="admin-logout-mobile"><LogOut size={20} /></button>
        </div>
        <div className="md:hidden flex bg-brand-green/5 border-b border-brand-green/10">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} className={`flex-1 py-3 text-sm ${tab === n.id ? "text-brand-gold font-medium" : "text-brand-green/60"}`}>
              {n.label}{n.badge > 0 ? ` (${n.badge})` : ""}
            </button>
          ))}
        </div>

        <main className="flex-1 p-6 sm:p-10 overflow-auto">
          {/* BOOKINGS */}
          {tab === "bookings" && (
            <div data-testid="bookings-panel">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-serif text-3xl text-brand-green">Bookings</h1>
                  <p className="text-brand-green/60 text-sm mt-1">{bookings.length} total · {pendingCount} pending</p>
                </div>
              </div>
              {bookings.length === 0 ? (
                <div className="text-center py-20 text-brand-green/50">No bookings yet.</div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((b) => (
                    <div key={b.id} className="bg-white rounded-2xl border border-brand-green/10 p-5" data-testid={`booking-row-${b.id}`}>
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-serif text-lg text-brand-green">{b.name}</h3>
                            <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_COLORS[b.status] || "bg-gray-100"}`}>{b.status}</span>
                          </div>
                          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-brand-green/70">
                            <span className="flex items-center gap-1.5"><Mail size={13} /> {b.email}</span>
                            <span className="flex items-center gap-1.5"><Phone size={13} /> {b.phone}</span>
                          </div>
                          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1.5 text-sm text-brand-green/70">
                            <span className="flex items-center gap-1.5"><Sparkles size={13} className="text-brand-gold" /> {b.treatment_name} · {b.treatment_currency}{b.treatment_price}</span>
                            <span className="flex items-center gap-1.5"><MapPin size={13} className="text-brand-gold" /> {b.location_name}</span>
                            <span className="flex items-center gap-1.5"><Clock size={13} className="text-brand-gold" /> {b.date} at {b.time}</span>
                          </div>
                          {b.notes && <p className="mt-2 text-sm text-brand-green/60 italic">"{b.notes}"</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Select value={b.status} onValueChange={(v) => setBookingStatus(b.id, v)}>
                            <SelectTrigger className="w-36" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                          <button onClick={() => deleteBooking(b.id)} data-testid={`delete-booking-${b.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TREATMENTS */}
          {tab === "treatments" && (
            <div data-testid="treatments-panel">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="font-serif text-3xl text-brand-green">Treatments</h1>
                  <p className="text-brand-green/60 text-sm mt-1">Manage names, pricing & duration</p>
                </div>
                <button onClick={openCreate} data-testid="add-treatment-button" className="flex items-center gap-2 bg-brand-green text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-brand-greenDark transition-all">
                  <Plus size={16} /> Add Treatment
                </button>
              </div>

              {CATEGORY_ORDER.map((cat) => {
                const items = treatments.filter((t) => t.category === cat);
                if (items.length === 0) return null;
                return (
                  <div key={cat} className="mb-8">
                    <h2 className="font-serif text-lg text-brand-gold mb-3">{cat}</h2>
                    <div className="bg-white rounded-2xl border border-brand-green/10 divide-y divide-brand-green/10">
                      {items.map((t) => (
                        <div key={t.id} className="p-4 flex items-center gap-4" data-testid={`treatment-row-${t.id}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-brand-green">{t.name}</span>
                              {!t.active && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">hidden</span>}
                            </div>
                            <p className="text-sm text-brand-green/55 mt-0.5 line-clamp-1">{t.description}</p>
                          </div>
                          <span className="text-sm text-brand-green/70 flex items-center gap-1"><Clock size={13} /> {t.duration_minutes}m</span>
                          <span className="font-serif text-brand-gold w-16 text-right">{t.currency}{t.price}</span>
                          <button onClick={() => openEdit(t)} data-testid={`edit-treatment-${t.id}`} className="p-2 text-brand-green hover:bg-brand-green/5 rounded-lg"><Pencil size={16} /></button>
                          <button onClick={() => deleteTreatment(t.id)} data-testid={`delete-treatment-${t.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={16} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* SETTINGS */}
          {tab === "settings" && (
            <div data-testid="settings-panel" className="max-w-xl">
              <h1 className="font-serif text-3xl text-brand-green mb-2">Settings</h1>
              <p className="text-brand-green/60 text-sm mb-8">Configure where booking requests are sent.</p>
              <div className="bg-white rounded-2xl border border-brand-green/10 p-6 space-y-5">
                <div>
                  <Label htmlFor="booking_email">Booking notification email *</Label>
                  <Input id="booking_email" type="email" data-testid="settings-email-input" value={settings.booking_email}
                    onChange={(e) => setSettings({ ...settings, booking_email: e.target.value })} className="mt-1.5" />
                  <p className="text-xs text-brand-green/50 mt-1.5">New booking requests are associated with this address.</p>
                </div>
                <div>
                  <Label htmlFor="clinic_name">Clinic name</Label>
                  <Input id="clinic_name" data-testid="settings-clinic-input" value={settings.clinic_name || ""}
                    onChange={(e) => setSettings({ ...settings, clinic_name: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Contact phone</Label>
                  <Input id="contact_phone" data-testid="settings-phone-input" value={settings.contact_phone || ""}
                    onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })} className="mt-1.5" />
                </div>
                <button onClick={saveSettings} data-testid="save-settings-button" className="flex items-center gap-2 bg-brand-green text-white rounded-full px-7 py-2.5 text-sm font-medium hover:bg-brand-greenDark transition-all">
                  <Save size={16} /> Save Settings
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Treatment dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="treatment-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-brand-green text-xl">{editing ? "Edit Treatment" : "Add Treatment"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name</Label>
              <Input data-testid="tform-name" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={tForm.category} onValueChange={(v) => setTForm({ ...tForm, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="tform-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="tform-description" value={tForm.description} onChange={(e) => setTForm({ ...tForm, description: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input data-testid="tform-image" value={tForm.image || ""} onChange={(e) => setTForm({ ...tForm, image: e.target.value })} className="mt-1.5" placeholder="https://... (leave blank for default)" />
              {tForm.image ? (
                <img src={tForm.image} alt="preview" className="mt-2 h-20 w-full object-cover rounded-lg border border-brand-green/10" />
              ) : null}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Currency</Label>
                <Select value={tForm.currency} onValueChange={(v) => setTForm({ ...tForm, currency: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="tform-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="£">£ GBP</SelectItem>
                    <SelectItem value="₹">₹ INR</SelectItem>
                    <SelectItem value="$">$ USD</SelectItem>
                    <SelectItem value="€">€ EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price</Label>
                <Input type="number" data-testid="tform-price" value={tForm.price} onChange={(e) => setTForm({ ...tForm, price: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" data-testid="tform-duration" value={tForm.duration_minutes} onChange={(e) => setTForm({ ...tForm, duration_minutes: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label>Display order</Label>
                <Input type="number" data-testid="tform-order" value={tForm.order} onChange={(e) => setTForm({ ...tForm, order: e.target.value })} className="mt-1.5" />
              </div>
              <label className="flex items-center gap-2 text-sm text-brand-green pb-2.5">
                <input type="checkbox" data-testid="tform-active" checked={tForm.active} onChange={(e) => setTForm({ ...tForm, active: e.target.checked })} className="w-4 h-4 accent-[#1F4A34]" />
                Visible on website
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-5 py-2.5 text-sm text-brand-green/70 rounded-full hover:bg-brand-green/5">Cancel</button>
            <button onClick={saveTreatment} data-testid="tform-save" className="bg-brand-green text-white rounded-full px-7 py-2.5 text-sm font-medium hover:bg-brand-greenDark transition-all">
              {editing ? "Update" : "Add"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
